import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { CreditCard, AlertTriangle, Lock } from "lucide-react";

const SubscriptionContext = createContext({ status: null, loading: true });

export function useSubscription() {
  return useContext(SubscriptionContext);
}

const ALWAYS_ALLOWED = ["/login", "/billing", "/billing/success", "/change-password", "/logout"];
const BYPASS_EMAILS = ["daniel@wearebudadvisors.com", "fernando@wearebudadvisors.com"];
const FULL_ACCESS = new Set(["bypass", "bypass_billing_disabled", "trialing", "active"]);

export function SubscriptionProvider({ children, userEmail }) {
  const [subStatus, setSubStatus] = useState({ status: null, loading: true, periodEnd: null, cancelAt: false });

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/billing/subscription-status`);
      setSubStatus({ status: r.data.status, loading: false, periodEnd: r.data.period_end, cancelAt: r.data.cancel_at_period_end });
    } catch {
      // If billing endpoint fails, don't block — assume bypass for safety
      setSubStatus({ status: BYPASS_EMAILS.includes(userEmail) ? "bypass" : "inactive", loading: false, periodEnd: null, cancelAt: false });
    }
  }, [userEmail]);

  useEffect(() => { if (userEmail) refresh(); }, [userEmail, refresh]);

  return (
    <SubscriptionContext.Provider value={{ ...subStatus, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function SubscriptionGuard({ children }) {
  const { status, loading } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();

  // Always allow certain routes
  if (ALWAYS_ALLOWED.some(p => location.pathname.startsWith(p))) return children;

  // Loading — show nothing (parent already renders layout)
  if (loading) return children;

  // Full access
  if (FULL_ACCESS.has(status)) return children;

  // past_due — show warning banner but allow access
  if (status === "past_due") {
    return (
      <>
        <div className="sticky top-0 z-50 bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between" data-testid="past-due-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-800">Pago pendiente. Actualiza tu método de pago para mantener el acceso completo.</span>
          </div>
          <Button size="sm" variant="outline" className="text-xs border-amber-300 text-amber-700" onClick={() => navigate("/billing")}>
            <CreditCard className="w-3 h-3 mr-1" /> Gestionar facturación
          </Button>
        </div>
        {children}
      </>
    );
  }

  // inactive / canceled — paywall
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="subscription-paywall">
      <div className="text-center max-w-md space-y-4 p-8">
        <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: "#E1C42215" }}>
          <Lock className="w-7 h-7" style={{ color: "#E1C422" }} />
        </div>
        <h2 className="text-xl font-serif" style={{ color: "#0B1D3A" }}>Activa tu suscripción</h2>
        <p className="text-sm text-muted-foreground">
          Necesitas una suscripción activa a Valuo.pro para acceder a esta sección.
          Incluye 7 días de prueba gratuita.
        </p>
        <Button onClick={() => navigate("/billing")} className="gap-2" style={{ backgroundColor: "#E1C422", color: "#0B1D3A" }} data-testid="paywall-cta">
          <CreditCard className="w-4 h-4" /> Ir a facturación
        </Button>
        <p className="text-xs text-muted-foreground">
          Valuo.pro Analyst — 499 €/mes
        </p>
      </div>
    </div>
  );
}
